import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'gradient' | 'ghost' | 'outline' | 'white';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  href?: string;
  className?: string;
  as?: 'button' | 'link' | 'a';
  target?: string;
  rel?: string;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconPosition = 'left',
  href,
  className = '',
  as = 'button',
  target,
  rel,
  ...props
}: ButtonProps) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 whitespace-nowrap';

  const variantClasses = {
    primary: 'bg-[#d03484] text-white hover:bg-[#d47254] focus:ring-[#d03484] hover-lift',
    secondary: 'bg-white border-2 border-[#d03484] text-[#d03484] hover:bg-[#d03484]/5 focus:ring-[#d03484] hover-lift',
    gradient: 'bg-gradient-to-r from-[#d03484] to-[#d47254] text-white hover:opacity-90 focus:ring-[#d03484] hover-lift',
    ghost: 'bg-transparent hover:bg-neutral-100 text-neutral-700 hover-lift',
    outline: 'bg-transparent border-2 border-neutral-200 text-neutral-700 hover:border-[#d03484] hover:text-[#d03484] hover-lift',
    white: 'bg-white text-neutral-900 hover:bg-neutral-50 focus:ring-white hover-lift',
  };

  const sizeClasses = {
    sm: 'text-sm px-3 py-1.5',
    md: 'text-sm px-4 py-2',
    lg: 'text-base px-5 py-2.5',
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const allClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClasses} ${className}`;

  if (href || as === 'link' || as === 'a') {
    const isExternal = href && (href.startsWith('http') || href.startsWith('https'));
    const linkTarget = target || (isExternal ? '_blank' : undefined);
    const linkRel = rel || (isExternal ? 'noopener noreferrer' : undefined);

    return (
      <a
        href={href}
        target={linkTarget}
        rel={linkRel}
        className={allClasses}
      >
        {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
        {children}
        {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
      </a>
    );
  }

  return (
    <button
      className={allClasses}
      {...props}
    >
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </button>
  );
};

export default Button;